import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { RootState, AppDispatch } from '@/store';
import { fetchCategoryTree } from '@/store/slices/categoriesSlice';
import { Category } from '@/types';
import { cn } from '@/utils/cn';
import { getImageUrl } from '@/utils/imageUtils';

interface CategorySidebarProps {
  className?: string;
  collapsible?: boolean;
}

export const CategorySidebar = ({ className, collapsible = true }: CategorySidebarProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { categoryTree, isTreeLoading } = useSelector((state: RootState) => state.categories);
  const { categorySlug, parentSlug, childSlug } = useParams();

  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Fetch category tree on mount
  useEffect(() => {
    console.log('CategorySidebar mounted, categoryTree:', categoryTree.length, 'isTreeLoading:', isTreeLoading);
    if (categoryTree.length === 0 && !isTreeLoading) {
      console.log('Dispatching fetchCategoryTree');
      dispatch(fetchCategoryTree());
    }
  }, [dispatch, categoryTree.length, isTreeLoading]);

  // Debug log when category tree changes
  useEffect(() => {
    console.log('CategorySidebar categoryTree changed:', categoryTree);
  }, [categoryTree]);

  // Auto-expand categories based on current route
  useEffect(() => {
    const activeSlug = childSlug || categorySlug || parentSlug;
    if (activeSlug && categoryTree.length > 0) {
      const expanded = new Set<number>();

      // Find the active category and expand its parents
      const findAndExpandParents = (categories: Category[], targetSlug: string): boolean => {
        for (const category of categories) {
          if (category.slug === targetSlug) {
            expanded.add(category.id);
            return true;
          }

          if (category.children && category.children.length > 0) {
            if (findAndExpandParents(category.children, targetSlug)) {
              expanded.add(category.id);
              return true;
            }
          }
        }
        return false;
      };

      findAndExpandParents(categoryTree, activeSlug);
      setExpandedCategories(expanded);
    }
  }, [categorySlug, parentSlug, childSlug, categoryTree]);

  const toggleCategory = (categoryId: number) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const isActive = (slug: string) => {
    return slug === categorySlug || slug === parentSlug || slug === childSlug;
  };

  const renderCategory = (category: Category, level: number = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const active = isActive(category.slug);

    return (
      <div key={category.id} className="w-full">
        <div
          className={cn(
            'flex items-center gap-2 w-full text-left transition-colors duration-150',
            level === 0 ? 'py-2.5' : 'py-2',
            active
              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'
          )}
          style={{ paddingLeft: `${level * 16 + 12}px` }}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.preventDefault();
                toggleCategory(category.id);
              }}
              className="p-1 hover:bg-gray-200 dark:hover:bg-dark-600 rounded transition-colors"
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
          )}

          {!hasChildren && <div className="w-6" />}

          <Link
            to={`/category/${category.slug}`}
            className="flex-1 flex items-center gap-2"
          >
            {category.image_url ? (
              <img
                src={getImageUrl(category.image_url)}
                alt={category.name}
                className="w-6 h-6 rounded object-cover flex-shrink-0"
                loading="lazy"
              />
            ) : (
              <div className="w-6 h-6 rounded bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/20 dark:to-primary-800/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-primary-600 dark:text-primary-400">
                  {category.name[0]}
                </span>
              </div>
            )}
            <span className={cn('text-sm', level === 0 && 'font-medium')}>
              {category.name}
            </span>
          </Link>
        </div>

        {/* Render children with animation */}
        <AnimatePresence>
          {hasChildren && isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              {category.children?.map((child) => renderCategory(child, level + 1))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  if (isCollapsed && collapsible) {
    return (
      <div className={cn('flex flex-col', className)}>
        <button
          onClick={() => setIsCollapsed(false)}
          className="p-3 bg-white dark:bg-dark-850 border border-gray-200 dark:border-dark-700 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors"
          title="Expand Categories"
        >
          <Bars3Icon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'bg-white dark:bg-dark-850 border border-gray-200 dark:border-dark-700 rounded-lg overflow-hidden',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-700">
        <div className="flex items-center gap-2">
          <Bars3Icon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            All Categories
          </h2>
        </div>
        {collapsible && (
          <button
            onClick={() => setIsCollapsed(true)}
            className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded transition-colors"
            title="Collapse"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        )}
      </div>

      {/* Category List */}
      <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
        {isTreeLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="h-10 bg-gray-200 dark:bg-dark-700 rounded animate-pulse"
              />
            ))}
          </div>
        ) : categoryTree.length === 0 ? (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">
            No categories available
          </div>
        ) : (
          <div className="py-2">
            {categoryTree.map((category) => renderCategory(category))}
          </div>
        )}
      </div>
    </div>
  );
};

// Mobile version with slide-in panel
interface CategorySidebarMobileProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CategorySidebarMobile = ({ isOpen, onClose }: CategorySidebarMobileProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={onClose}
          />

          {/* Sidebar Panel */}
          <motion.div
            initial={{ x: -320 }}
            animate={{ x: 0 }}
            exit={{ x: -320 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed left-0 top-0 bottom-0 w-80 bg-white dark:bg-dark-850 z-50 lg:hidden overflow-y-auto"
          >
            <CategorySidebar collapsible={false} className="border-0 rounded-none h-full" />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CategorySidebar;
